const ROLE_GOALS = {
  developer: 'Focus on finishing in-progress stories and reducing carryover work.',
  tester: 'Focus on validating high-risk items early and closing verification loops quickly.',
  designer: 'Focus on unblocking handoffs and finalizing priority UI deliverables.',
  devops: 'Focus on delivery stability, deployment readiness, and blocker removal.',
  analyst: 'Focus on clarifying requirements and reducing ambiguity for active tasks.',
  lead: 'Focus on unblocking the team, code review throughput, and sprint goal delivery.',
  product: 'Focus on backlog clarity, stakeholder alignment, and sprint commitments.',
  product_manager: 'Focus on roadmap alignment, backlog clarity, and sprint goal achievement.',
  manager: 'Focus on team throughput, blocker removal, and predictable sprint delivery.',
  marketing_manager: 'Focus on campaign milestones, pipeline goals, and cross-channel delivery in the active sprint.',
  brand_manager: 'Focus on brand campaign tasks and creative approvals tied to sprint timelines.',
  pr_manager: 'Focus on PR deliverables, media timelines, and stakeholder communications.',
  content_marketer: 'Focus on content tasks in the current sprint and campaign deadlines.',
  marketing_designer: 'Focus on creative deliverables tied to the active sprint timeline.',
  sales_marketing: 'Focus on sprint-aligned marketing execution and stakeholder updates.',
  member: 'Focus on steady delivery and timely closure of assigned customer success work.',
  trainee: 'Focus on learning sprint rhythm and closing assigned tasks on time.',
  hr: 'Focus on people programs and HR deliverables on schedule.',
  it_support: 'Focus on ticket resolution and infrastructure readiness for active work.',
};

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

/**
 * Shared Smart Review + workload mix inputs (IC dashboard and manager profile modal).
 */
export function buildMemberDashboardInsights({
  myWork = {},
  blockers = {},
  currentSprint = null,
  statusBreakdown = [],
  memberRole = '',
  stats = null,
  perspective = 'self',
}) {
  const assignedCount = Number(myWork.assigned || 0);
  const completedCount = Number(myWork.completed || 0);
  const inProgressCount = Number(myWork.inProgress || 0);
  const testingCount = Number(myWork.testing || 0);
  const pendingCount = Math.max(assignedCount - completedCount - inProgressCount, 0);
  const completionRate = assignedCount > 0 ? Math.round((completedCount / assignedCount) * 100) : 0;
  const sprintActual = Number(currentSprint?.progressActual || 0);
  const sprintExpected = Number(currentSprint?.progressExpected || 0);
  const paceGap = sprintActual - sprintExpected;
  const blockerCount = Math.max(
    Number(blockers.count || 0),
    Number(stats?.blockers_open || 0),
  );
  const criticalBlockers = Number(blockers.critical || 0);
  const roleKey = String(memberRole || '').toLowerCase();
  const isManager = perspective === 'manager';

  const statusCounts = (statusBreakdown || []).reduce((acc, item) => {
    const key = String(item?.status || '').toLowerCase();
    acc[key] = Number(item?.count || 0);
    return acc;
  }, {});

  const laggingStatuses = ['blocked', 'reopened', 'testing', 'in progress'];
  const laggingQueue = laggingStatuses
    .map((status) => ({ status, count: statusCounts[status] || 0 }))
    .filter((item) => item.count > 0)
    .sort((a, b) => b.count - a.count);

  const myWorkMix = [
    { label: 'Completed', value: completedCount, color: '#10b981' },
    { label: 'In Progress', value: inProgressCount, color: '#2C8FC7' },
    { label: 'Pending', value: pendingCount, color: '#f59e0b' },
  ];

  const roleGoal = ROLE_GOALS[roleKey]
    || 'Focus on steady delivery, communication, and timely closure of assigned work.';

  const confidenceScore = clamp(
    Math.round(72 + (paceGap * 1.2) + (completionRate * 0.15) - (blockerCount * 4)),
    35,
    98,
  );

  let momentumMessage;
  if (blockerCount === 0 && paceGap >= 0) {
    momentumMessage = isManager
      ? 'Strong momentum on active sprint work — pace is on or ahead of plan.'
      : 'Great momentum. Keep the same pace and continue closing active items.';
  } else if (blockerCount <= 2 && paceGap > -8) {
    momentumMessage = isManager
      ? 'Progress is generally healthy. A few follow-ups should keep delivery on track.'
      : 'Progress is healthy. A few focused follow-ups can keep you on track.';
  } else {
    momentumMessage = isManager
      ? 'Delivery is in a recoverable zone — prioritize blocker removal and near-complete items.'
      : 'You are still in a recoverable zone. Prioritize blocker removal and close one high-impact item today.';
  }

  const focusRecommendations = [
    isManager ? `Role focus: ${roleGoal}` : roleGoal,
    blockerCount > 0
      ? `${blockerCount} blocker${blockerCount > 1 ? 's' : ''} open (${criticalBlockers} critical). ${isManager ? 'Follow up with the assignee and escalate if needed.' : 'Follow up with owners and escalate quickly if no movement.'}`
      : isManager
        ? 'No active blockers on their queue right now.'
        : 'No active blockers right now. Keep protecting this by resolving dependencies early.',
    currentSprint
      ? (paceGap < 0
        ? `Sprint pace is ${Math.abs(paceGap)}% behind expected. ${isManager ? 'Consider rebalancing workload or unblocking dependencies.' : 'Shift effort to near-complete tasks to improve closure rate.'}`
        : `Sprint pace is ${paceGap}% ahead of expected. ${isManager ? 'Maintain this consistency and limit unplanned scope.' : 'Keep this consistency and avoid new unplanned work.'}`)
      : `${isManager ? 'They have' : 'You have'} ${assignedCount} assigned item${assignedCount !== 1 ? 's' : ''} across projects — ${completionRate}% complete so far.`,
    laggingQueue.length > 0
      ? `Lagging queue: ${laggingQueue.slice(0, 3).map((i) => `${i.status} (${i.count})`).join(', ')}.`
      : isManager
        ? 'Lagging queue is under control.'
        : 'Lagging queue is under control. Continue regular daily status updates with your team.',
  ];

  return {
    assignedCount,
    completedCount,
    inProgressCount,
    testingCount,
    pendingCount,
    completionRate,
    blockerCount,
    criticalBlockers,
    confidenceScore,
    momentumMessage,
    focusRecommendations,
    myWorkMix,
  };
}

const issueTitle = (row) => {
  const line = String(row?.description || '').split('\n')[0]?.trim();
  return line || 'Untitled item';
};

/**
 * Structured summary bullets for Performance tab (completed tasks & stories).
 */
export function buildCompletedWorkSummary({
  stats = {},
  pastWork = [],
  sprintTimeline = [],
  showBugs = false,
}) {
  const tasksClosed = Number(stats.tasks_closed || 0);
  const storiesClosed = Number(stats.stories_closed || 0);
  const bugsClosed = Number(stats.bugs_closed || 0);
  const totalClosed = Number(stats.total_closed || 0);
  const storyPointsDone = Number(stats.story_points_done || 0);
  const closedWeek = Number(stats.closed_this_week || 0);
  const closedMonth = Number(stats.closed_this_month || 0);
  const avgDays = stats.avg_days_to_close;

  const bullets = [];

  if (totalClosed === 0) {
    bullets.push('No completed tasks or stories recorded yet.');
    return { bullets, highlights: [] };
  }

  const typeParts = [];
  if (tasksClosed > 0) typeParts.push(`${tasksClosed} task${tasksClosed !== 1 ? 's' : ''}`);
  if (storiesClosed > 0) typeParts.push(`${storiesClosed} stor${storiesClosed !== 1 ? 'ies' : 'y'}`);
  if (showBugs && bugsClosed > 0) typeParts.push(`${bugsClosed} bug${bugsClosed !== 1 ? 's' : ''}`);

  if (typeParts.length > 0) {
    bullets.push(`Completed ${typeParts.join(' and ')} (${storyPointsDone} story points delivered).`);
  } else {
    bullets.push(`Completed ${totalClosed} items (${storyPointsDone} story points delivered).`);
  }

  const paceParts = [];
  if (closedWeek > 0) paceParts.push(`${closedWeek} this week`);
  if (closedMonth > 0) paceParts.push(`${closedMonth} this month`);
  if (paceParts.length > 0) {
    const avgPart = avgDays != null ? ` · avg ${avgDays} days to close` : '';
    bullets.push(`Recent pace: ${paceParts.join(', ')}${avgPart}.`);
  } else if (avgDays != null) {
    bullets.push(`Average ${avgDays} days to close completed work.`);
  }

  const sprintLines = (sprintTimeline || [])
    .filter((s) => Number(s.done) > 0)
    .slice(0, 3)
    .map((s) => {
      const total = Number(s.total || 0);
      const done = Number(s.done || 0);
      const pct = total > 0 ? Math.round((done / total) * 100) : 0;
      return `${s.sprint_name} (${done}/${total}, ${pct}%)`;
    });
  if (sprintLines.length > 0) {
    bullets.push(`Sprint contributions: ${sprintLines.join(' · ')}.`);
  }

  const highlights = (pastWork || [])
    .filter((row) => {
      const t = String(row?.issuetype || '').toLowerCase();
      if (t === 'task' || t === 'story') return true;
      return showBugs && t === 'bug';
    })
    .slice(0, 8)
    .map((row) => ({
      title: issueTitle(row),
      type: row.issuetype || 'Item',
      sprint: row.sprint_name || 'Backlog',
      points: row.story_points != null ? Number(row.story_points) : null,
    }));

  return { bullets, highlights };
}
